import React, { useState } from "react";
import { File, FilePlus, Printer, Save, X, Upload } from "lucide-react";
import { loadImage } from "../../common/services/uiService";
import UnsavedChangesModal from "./UnsavedChangesModal";

const AdminToolbar = ({
  activeTab,
  onSave,
  onDelete,
  onAdd,
  hasChanges = false,
  isSaving = false,
  selectedItem = null,
  isAdding = false,
  isDeleting = false,
  isBottomSaving = false,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const loadingIcon = loadImage("loading_continuous.gif");

  const isGroupsOrUsers = activeTab === "groups" || activeTab === "users";
  const isPermissions = activeTab === "permissions";

  // Check if selected item is the protected Administrators group
  const isAdministratorsGroup =
    activeTab === "groups" &&
    selectedItem &&
    selectedItem.name === "Administrators";

  const iconClass = "h-5 w-5";
  const buttonBaseClass =
    "flex items-center justify-center p-2 rounded transition-colors duration-200";
  const enabledClass =
    "hover:bg-customLightBlue text-customBlue cursor-pointer";
  const disabledClass = "text-lightgray cursor-not-allowed";

  const getButtonClass = (isEnabled) => {
    return `${buttonBaseClass} ${isEnabled ? enabledClass : disabledClass}`;
  };

  const handleAdd = () => {
    if (onAdd && !isAdding) {
      onAdd();
    }
  };

  const handleSave = () => {
    if (onSave && hasChanges && !isSaving && !isBottomSaving) {
      onSave();
    }
  };

  const handleDelete = () => {
    // Prevent deletion of Administrators group
    if (isAdministratorsGroup) {
      return;
    }

    if (onDelete && selectedItem && !isDeleting) {
      setShowDeleteModal(true);
    }
  };

  const handleModalDelete = async () => {
    if (onDelete && selectedItem && !isAdministratorsGroup) {
      await onDelete(selectedItem);
    }
    setShowDeleteModal(false);
  };

  const handleModalCancel = () => {
    setShowDeleteModal(false);
  };

  const getDeleteMessage = () => {
    if (!selectedItem) return "";
    const itemType = activeTab === "groups" ? "group" : "user";
    return `Are you sure you want to delete the ${itemType} "${selectedItem.name}"?`;
  };

  // Delete button should be disabled for Administrators group, when no item selected, or when deleting
  const isDeleteEnabled =
    isGroupsOrUsers && selectedItem && !isAdministratorsGroup && !isDeleting;

  // Save button should be enabled only when there are changes and not currently saving (from either toolbar or bottom)
  const isSaveEnabled = hasChanges && !isSaving && !isBottomSaving;

  // Add button should be enabled for Groups and Users and not currently adding
  const isAddEnabled = isGroupsOrUsers && !isAdding;

  return (
    <>
      <div className="flex items-center gap-1 px-4 py-2 bg-white border-b border-gray-200">
        {/* File icon - Always disabled */}
        <button className={getButtonClass(false)} disabled>
          <File className={iconClass} />
        </button>

        {/* Plus in file icon - Enabled for Groups and Users */}
        <button
          className={getButtonClass(isAddEnabled)}
          disabled={!isAddEnabled}
          onClick={handleAdd}
          title={
            isAdding
              ? "Adding..."
              : isGroupsOrUsers
              ? `Add new ${activeTab.slice(0, -1)}`
              : ""
          }
        >
          {isAdding ? (
            <img
              src={loadingIcon}
              alt="loading"
              className="h-5 w-5 object-cover"
            />
          ) : (
            <FilePlus className={iconClass} />
          )}
        </button>

        {/* Printer icon - Always disabled */}
        <button className={getButtonClass(false)} disabled>
          <Printer className={iconClass} />
        </button>

        {/* Save icon - Enabled only when there are changes and not saving */}
        <button
          className={getButtonClass(isSaveEnabled)}
          onClick={handleSave}
          disabled={!isSaveEnabled}
          title={
            !hasChanges
              ? "No changes to save"
              : isSaving
              ? "Saving..."
              : isBottomSaving
              ? "Save in progress..."
              : "Save changes"
          }
        >
          {isSaving ? (
            <img
              src={loadingIcon}
              alt="loading"
              className="h-5 w-5 object-cover"
            />
          ) : (
            <Save className={iconClass} />
          )}
        </button>

        {/* X icon - Disabled for Administrators group */}
        <div className="relative group">
          <button
            className={getButtonClass(isDeleteEnabled)}
            disabled={!isDeleteEnabled}
            onClick={handleDelete}
            title={
              isDeleting
                ? "Deleting..."
                : isAdministratorsGroup
                ? "Cannot delete Administrators group"
                : isDeleteEnabled
                ? `Delete ${selectedItem.name}`
                : ""
            }
          >
            {isDeleting ? (
              <img
                src={loadingIcon}
                alt="loading"
                className="h-5 w-5 object-cover"
              />
            ) : (
              <div className="relative flex items-center justify-center w-5 h-5">
                {/* Circle border that changes color based on enabled/disabled state */}
                <div
                  className={`absolute inset-0 w-5 h-5 border-2 rounded-full ${
                    isDeleteEnabled ? "border-customBlue" : "border-gray-300"
                  }`}
                ></div>
                {/* X icon on top */}
                <X className="h-4 w-4 relative z-10" />
              </div>
            )}
          </button>

          {/* Tooltip for Administrators group */}
          {isAdministratorsGroup && !isDeleting && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-10">
              Cannot delete Administrators group
            </div>
          )}
        </div>

        {/* Export icon - Always disabled */}
        <button className={getButtonClass(false)} disabled>
          <Upload className={iconClass} />
        </button>
      </div>

      <UnsavedChangesModal
        show={showDeleteModal}
        onHide={handleModalCancel}
        onLeftAction={handleModalDelete}
        onRightAction={handleModalCancel}
        message={getDeleteMessage()}
        leftLabel="Delete"
        rightLabel="Cancel"
      />
    </>
  );
};

export default AdminToolbar;
